/**
 * ConstructorProjectsPanel — admin approval de proyectos de vivienda nueva.
 *
 * Muestra tabs (pending / approved / rejected), tabla, y un panel lateral con
 * el detalle del proyecto (incluyendo tipologías) + acciones Aprobar/Rechazar.
 */
import React, { useEffect, useState } from 'react';
import {
  Building2, CheckCircle2, XCircle, Clock, ExternalLink, FileText, DollarSign,
  User, Mail, Phone, MapPin, Loader2, RefreshCw,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';

const STATUS_TABS = [
  { value: 'pending', label: 'Pendientes', icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'approved', label: 'Aprobados', icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { value: 'rejected', label: 'Rechazados', icon: XCircle, color: 'text-rose-600 bg-rose-50 border-rose-200' },
];

const fmtCOP = (n) => n == null ? '—' : Number(n).toLocaleString('es-CO');
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

export default function ConstructorProjectsPanel() {
  const { toast } = useToast();
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState({ items: [], counts: { pending: 0, approved: 0, rejected: 0 } });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // detailed project
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listConstructorProjects(status);
      setData(res);
    } catch (e) {
      toast({ title: 'Error cargando proyectos', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  const openDetail = async (item) => {
    setSelected({ ...item, typologies: null }); // show shell immediately
    setReason('');
    setSelectedLoading(true);
    try {
      const full = await api.getConstructorProject(item.id);
      setSelected(full);
    } catch (e) {
      toast({ title: 'Error cargando detalle', description: e.message, variant: 'destructive' });
    } finally {
      setSelectedLoading(false);
    }
  };

  const approve = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.approveConstructorProject(selected.id);
      toast({ title: 'Proyecto aprobado', description: 'Ya está visible para el Advisor.' });
      setSelected(null);
      await load();
    } catch (e) {
      toast({ title: 'No pudimos aprobar', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!selected) return;
    if (!reason.trim()) {
      toast({ title: 'Falta el motivo', description: 'Indica por qué rechazas este proyecto.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await api.rejectConstructorProject(selected.id, reason.trim());
      toast({ title: 'Proyecto rechazado', description: 'Registramos el motivo.' });
      setSelected(null);
      await load();
    } catch (e) {
      toast({ title: 'No pudimos rechazar', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="constructor-projects-admin-panel">
      {/* Intro */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-habita-mint text-habita grid place-items-center shrink-0">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-serif text-xl font-bold text-habita-dark">Proyectos de vivienda nueva</h2>
            <p className="text-sm text-gray-600 mt-1">
              Revisa y aprueba los envíos de constructoras desde <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">/panel-constructoras</code>. 
              Solo los proyectos <strong>aprobados</strong> aparecen en las recomendaciones del Advisor.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs de estado */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map((t) => {
          const active = status === t.value;
          const count = data.counts?.[t.value] ?? 0;
          const Icon = t.icon;
          return (
            <button
              key={t.value}
              onClick={() => setStatus(t.value)}
              data-testid={`status-tab-${t.value}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition ${active ? t.color + ' font-semibold' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              <span className={`text-xs rounded-full px-2 py-0.5 ${active ? 'bg-white/70' : 'bg-gray-100'}`}>{count}</span>
            </button>
          );
        })}
        <button onClick={load} className="text-gray-500 hover:text-habita-dark ml-1" title="Recargar">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Lista */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Cargando...</div>
          ) : (data.items || []).length === 0 ? (
            <div className="p-10 text-center text-gray-500 text-sm">Sin proyectos en este estado.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {data.items.map((p) => {
                const isSel = selected?.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => openDetail(p)}
                    className={`w-full text-left p-4 transition ${isSel ? 'bg-habita-mint/30' : 'hover:bg-gray-50'}`}
                    data-testid={`project-row-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-habita-dark truncate">{p.project_name}</p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {p.company_name} · {p.city}{p.neighborhood ? ` · ${p.neighborhood}` : ''}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-600 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {p.typologies_count || 0} tipologías</span>
                          {p.beds_available && p.beds_available.length > 0 && (
                            <span>{p.beds_available.join(', ')} hab</span>
                          )}
                          {p.price_min && (
                            <span className="font-semibold text-habita">Desde ${fmtCOP(p.price_min)}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 shrink-0">{fmtDate(p.submitted_at)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detalle */}
        <div className="lg:col-span-1 bg-white rounded-2xl border border-gray-100 p-5 h-fit sticky top-4" data-testid="project-detail-panel">
          {!selected ? (
            <div className="text-sm text-gray-500 py-8 text-center">
              Selecciona un proyecto de la lista para ver el detalle.
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400">{selected.company_name}</p>
                <h3 className="font-serif text-xl text-habita-dark mt-0.5">{selected.project_name}</h3>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {selected.city}{selected.neighborhood ? ` · ${selected.neighborhood}` : ''}
                </p>
              </div>

              {/* Contacto */}
              <div className="bg-habita-cream/60 rounded-xl p-3 text-xs space-y-1.5">
                <p className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-habita" /> {selected.contact_name}</p>
                <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-habita" /> <a href={`mailto:${selected.contact_email}`} className="text-habita hover:underline">{selected.contact_email}</a></p>
                <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-habita" /> {selected.contact_phone}</p>
              </div>

              {/* Descripción */}
              {selected.description && (
                <p className="text-sm text-gray-700 whitespace-pre-line">{selected.description}</p>
              )}

              {/* Tipologías */}
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Tipologías</p>
                {selectedLoading || selected.typologies === null ? (
                  <p className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Cargando tipologías...</p>
                ) : (selected.typologies || []).length === 0 ? (
                  <p className="text-xs text-gray-400">Sin tipologías</p>
                ) : (
                  <div className="space-y-2" data-testid="typologies-list">
                    {selected.typologies.map((t) => (
                      <div key={t.id} className="rounded-lg border border-gray-200 p-2.5 text-xs">
                        <p className="font-semibold text-habita-dark">{t.name}</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1 text-gray-600">
                          <span>{t.area_m2} m²</span>
                          <span>{t.beds} hab</span>
                          <span>{t.baths} baños</span>
                          <span>{t.parking} parq</span>
                        </div>
                        <p className="mt-1.5 text-habita font-semibold">Desde ${fmtCOP(t.price_from)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Docs */}
              <div className="flex items-center gap-3 flex-wrap text-xs">
                {selected.brochure_url && (
                  <a href={selected.brochure_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-habita hover:underline" data-testid="brochure-link">
                    <FileText className="w-3.5 h-3.5" /> Brochure <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {selected.pricelist_url && (
                  <a href={selected.pricelist_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-habita hover:underline" data-testid="pricelist-link">
                    <DollarSign className="w-3.5 h-3.5" /> Lista de precios <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Acciones */}
              {selected.status === 'pending' ? (
                <div className="space-y-3 pt-3 border-t border-gray-100">
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Motivo (obligatorio para rechazar, opcional para aprobar)"
                    rows={2}
                    className="text-sm"
                    data-testid="reason-textarea"
                  />
                  <div className="flex items-center gap-2">
                    <Button onClick={approve} disabled={busy} className="btn-habita rounded-full h-9 px-4 flex-1" data-testid="approve-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> Aprobar
                    </Button>
                    <Button onClick={reject} disabled={busy} variant="outline" className="rounded-full h-9 px-4 flex-1 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="reject-btn">
                      <XCircle className="w-4 h-4 mr-1.5" /> Rechazar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="pt-3 border-t border-gray-100 text-xs">
                  <p className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${selected.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                    {selected.status === 'approved' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {selected.status === 'approved' ? 'Aprobado' : 'Rechazado'} · {fmtDate(selected.reviewed_at)}
                  </p>
                  {selected.admin_notes && (
                    <p className="mt-2 text-gray-600 italic">&ldquo;{selected.admin_notes}&rdquo;</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
